import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const categories = [
    { id: 'all', name: 'View All' },
    { id: 'color', name: 'Color & Highlights' },
    { id: 'styling', name: 'Styling' },
    { id: 'treatments', name: 'Treatments' },
    { id: 'updos', name: 'Updos & Events' }
  ];

  // Gallery items with responsive image sizes
  const galleryItems = [
    {
      id: 1,
      category: 'color',
      title: 'Balayage Transformation',
      description: 'Natural-looking, sun-kissed highlights with seamless blend',
      artist: 'Sarah Johnson',
      imageUrl: '/api/placeholder/600/800',
      size: 'large'
    },
    {
      id: 2,
      category: 'styling',
      title: 'Modern Bob Cut',
      description: 'Precision cut with textured ends for maximum movement',
      artist: 'Emily Chen',
      imageUrl: '/api/placeholder/600/400',
      size: 'small'
    },
    {
      id: 3,
      category: 'updos',
      title: 'Romantic Bridal Updo',
      description: 'Elegant twisted updo with delicate braiding details',
      artist: 'Michael Torres',
      imageUrl: '/api/placeholder/600/900',
      size: 'tall'
    },
  ];

  const filteredItems = selectedCategory === 'all'
    ? galleryItems
    : galleryItems.filter(item => item.category === selectedCategory);

  const handleImageClick = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const navigateImage = (direction) => {
    const currentIndex = filteredItems.findIndex(item => item.id === selectedImage.id);
    const newIndex = direction === 'next' 
      ? (currentIndex + 1) % filteredItems.length 
      : (currentIndex - 1 + filteredItems.length) % filteredItems.length;
    setIsLoading(true);
    setSelectedImage(filteredItems[newIndex]);
    setTimeout(() => setIsLoading(false), 300);
  };

  return (
    <main className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[41vh] sm:min-h-[40vh] flex items-end bg-[#F9F0F3] overflow-hidden pt-16 lg:pt-20">
        <div className="absolute inset-0 -top-16 lg:-top-20">
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent z-10" />
          <div className="grid grid-cols-2 sm:grid-cols-4 h-full opacity-50">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="relative h-full overflow-hidden">
                <img
                  src={`/api/placeholder/400/600`}
                  alt=""
                  className="w-full h-full object-cover scale-110"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 relative z-20 mb-8 sm:mb-16">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white mb-4">
            Our Creative
            <span className="block font-playfair italic">Portfolio</span>
          </h1>
        </div>
      </section>

      {/* Filter Categories */}
      <section className="sticky top-0 bg-white/90 backdrop-blur-md z-30 border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex gap-4 sm:gap-8 overflow-x-auto hide-scrollbar py-4 sm:py-6">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`whitespace-nowrap text-base sm:text-lg transition-all ${
                  selectedCategory === category.id
                    ? 'text-[#A47C89] font-medium scale-105'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-8 sm:py-12 lg:py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 sm:gap-6 lg:gap-8 space-y-4 sm:space-y-6 lg:space-y-8">
            {filteredItems.map(item => (
              <div
                key={item.id}
                className="break-inside-avoid group relative cursor-pointer"
                onClick={() => handleImageClick(item)}
              >
                <div className="relative overflow-hidden rounded-lg">
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    className="w-full transform transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <span className="text-[#FFB6C1] text-xs sm:text-sm uppercase tracking-wide block mb-2">
                      {item.category}
                    </span>
                    <h3 className="text-white text-lg sm:text-xl font-playfair mb-1">{item.title}</h3>
                    <p className="text-white/80 text-xs sm:text-sm">By {item.artist}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/95 z-50">
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <button 
              onClick={closeModal}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors z-50"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            
            {/* Navigation Buttons */}
            <button
              onClick={() => navigateImage('prev')}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-8 h-8 sm:w-10 sm:h-10" />
            </button>
            <button
              onClick={() => navigateImage('next')}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
            >
              <ChevronRight className="w-8 h-8 sm:w-10 sm:h-10" />
            </button>

            {/* Image and Details */}
            <div className="w-full max-w-6xl">
              <div className={`transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
                <div className="grid md:grid-cols-2 gap-4 sm:gap-8 items-center">
                  <div className="relative">
                    <img
                      src={selectedImage.imageUrl}
                      alt={selectedImage.title}
                      className="w-full rounded-lg"
                    />
                  </div>
                  <div className="text-white p-4 sm:p-8">
                    <span className="text-[#FFB6C1] text-xs sm:text-sm uppercase tracking-wide block mb-3 sm:mb-4">
                      {selectedImage.category}
                    </span>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-playfair mb-3 sm:mb-4">
                      {selectedImage.title}
                    </h2>
                    <p className="text-white/80 text-sm sm:text-base mb-4 sm:mb-6">
                      {selectedImage.description}
                    </p>
                    <div className="border-t border-white/20 pt-4 sm:pt-6">
                      <span className="text-white/60 text-sm block mb-2">Styled by</span>
                      <span className="text-lg sm:text-xl">{selectedImage.artist}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Gallery;